import axios from "axios";
import shortid from "shortid";
import Queue from "promise-queue";

import WP from "WP";
import { store } from "store";

import Middleware from "./Middleware";

/**
 * Post fetcher class
 *
 * Goal: never fetch the smae post twice. Class works with Redux store and keeps track of what has been loaded in the past.
 * Based on store state it adds 'exclude' param (posts already in store) to future requests, if posts are already in store they are returned emmedietly.
 *
 * Redux setup:
 *
 * const newsReducer = (state = {
 *                         posts: { no: [], en: [] },
 *                         allLoaded: { no: false, en: false },
 *                         latestLoaded: { no: false, en: false }
 *                     }, action) => {
 *     switch(action.type) {
 *         case 'SET_NEWS' :
 *             return { ...state, posts: { ...state.posts, [WP.lang]: action.payload } };
 *         case 'SET_NEWS_ALL_LOADED_FLAG' :
 *             return { ...state, allLoaded: { ...state.allLoaded, [WP.lang]: action.payload } };
 *         case 'SET_NEWS_LATEST_LOADED_FLAG' :
 *             return { ...state, latestLoaded: { ...state.latestLoaded, [WP.lang]: action.payload } };
 *         default :
 *             return state;
 *     }
 * }
 *
 * Init:
 *
 * const fetcher = new PostFetcher({
 *     storeKey: 'posts', // REQUIRED - global store key that holds the "posts" (and flags)
 *     fetchEndpoint: WP.api_base+'wp/v2/posts', // REQUIRED - endpoint for fetching post date
 *     debug: true, // OPTIONAL - console debug info on how requests are being handeled
 * });
 *
 * Usage:
 *
 * fetcher.getLatest({ limit: 5 }).then(...);
 * fetcher.getBySlug({ slug: '.....'}).then(...);
 * fetcher.getAll().then(...);
 * fetcher.getByIds({ ids: [], data: {} }).then(....);
 *
 *
 * @class PostFetcher
 */
class PostFetcher {
  constructor({ storeKey, fetchEndpoint, debug, revisionsFrom }) {
    this.storeKey = storeKey || "posts";
    this.revisionsFrom = revisionsFrom || "posts";

    // redux actions used to update store
    this.actions = {
      store: (posts) => ({ type: "SET_" + this.storeKey.toUpperCase(), payload: posts }),
      latestLoaded: (bool) => ({ type: "SET_" + this.storeKey.toUpperCase() + "_LATEST_LOADED_FLAG", payload: bool }),
      allLoaded: (bool) => ({ type: "SET_" + this.storeKey.toUpperCase() + "_ALL_LOADED_FLAG", payload: bool }),
    };

    this.fetchEndpoint = fetchEndpoint || WP.api_base + "wp/v2/posts";

    this.debug = debug;

    this.store = store; // provides access to redux store

    // promise queue (https://www.npmjs.com/package/promise-queue)
    const maxConcurrent = 1;
    const maxQueue = 100;
    this.queue = new Queue(maxConcurrent, maxQueue);

    this.middleware = new Middleware({ debug, revisionsFrom });
  }

  /**
   * Helper: Takes array of posts and returns array of posts ordered by publish date
   *
   * @param {Array} posts
   */
  orderPostsByDate(posts = []) {
    const copy = [...(posts || [])];
    copy.sort(function (a, b) {
      return new Date(b.post_date || b.date) - new Date(a.post_date || a.date);
    });
    return copy;
  }

  /**
   * Helper: Loops trough posts array and returns array of post IDs
   *
   * @param {Array} posts
   */
  collectPostIds(posts = []) {
    return posts.map((p) => p.id || p.ID);
  }

  /**
   * Helper: Logs messages to console if debug is enabled
   *
   * @param {String} message
   * @param {String} message2
   * @param {String} message3
   */
  log(message, message2 = "", message3 = "") {
    if (this.debug) console.log(message, message2, message3);
  }

  /**
   * Calls redux action that saves given posts to store
   *
   * @param {Array} posts
   */
  addPostsToStore(posts) {
    return this.storeAction(posts);
  }

  /**
   * Fetches posts using axios
   *
   * @param {Object} args - custom WP REST query args
   * @returns Promise
   * @memberof PostFetcher
   */
  getPosts(args = {}) {
    const defaultArgs = {
      per_page: 100,
      lang: WP.lang,
      stamp: shortid.generate(), // prevent safari query caching
    };

    let params = { ...defaultArgs, ...args };

    if (this.middleware.needsToByApplied()) {
      return this.middleware.apply();
    }

    this.log("PostFetcher::getPosts() URL: " + this.fetchEndpoint + " params:", params);

    // ... get posts by date
    return axios.get(this.fetchEndpoint, { params }); // always return posts as array (even if only one present)
  }

  /**
   * Gets all posts and sets them in store
   *
   * @param {Boolean} setFlags - weather or not set "allLoaded" / "latestLoaded" flegs in store after fetch
   * @param {Boolean} ignoreFlags - weather or not consider "allLoaded" store flag while checking store post status
   * @param {Object} args - additional args passed to fetch function
   * @returns Promise
   * @memberof PostFetcher
   */
  async getAll({ setFlags, ignoreFlags, args = {} } = {}) {
    return this.queue.add(async () => {
      const storeState = this.store.getState();
      const store = storeState[this.storeKey];

      //this.log('PostFetcher::getAll() -> storeState for "'+this.storeKey+'": ', store);

      if (store.allLoaded[WP.lang] && ignoreFlags !== true) {
        // all posts already in store  - return

        this.log("PostFetcher::getAll() -> all posts already in store");

        return Promise.resolve({ data: store.posts[WP.lang] });
      } else {
        if (store.posts[WP.lang].length > 0) {
          // some posts already in store (but not all) - fetch remaining

          this.log(
            "PostFetcher::getAll() -> some posts already in store (" +
              store.posts[WP.lang].length +
              ") - fetching rest..."
          );

          let remaining = await this.getPosts({
            exclude: this.collectPostIds(store.posts[WP.lang]).join(","),
            ...args,
          });

          this.log("PostFetcher::getAll() -> ...fetch result (remaining posts): ", remaining);

          const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(remaining.data || [])]);

          this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store
          if (setFlags !== false) this.store.dispatch(this.actions.allLoaded(true)); // update store flag
          if (setFlags !== false) this.store.dispatch(this.actions.latestLoaded(true)); // update store flag

          return Promise.resolve({ data: combinedPosts });
        } else {
          // store does not have any posts - fetch all

          this.log("PostFetcher::getAll() -> no posts in store - fetching all...");

          let all = await this.getPosts(args);

          this.log("PostFetcher::getAll() -> ...fetch result (all posts): ", all);

          this.store.dispatch(this.actions.store(all.data || [])); // set posts in store
          if (setFlags !== false) this.store.dispatch(this.actions.allLoaded(true)); // update store flag
          if (setFlags !== false) this.store.dispatch(this.actions.latestLoaded(true)); // update store flag

          return Promise.resolve(all);
        }
      }
    });
  }

  /**
   * Gets all public (non-private) posts and sets them in store
   * This method filters out posts where acf.private is true
   *
   * @param {Boolean} setFlags - weather or not set "allLoaded" / "latestLoaded" flegs in store after fetch
   * @param {Boolean} ignoreFlags - weather or not consider "allLoaded" store flag while checking store post status
   * @param {Object} args - additional args passed to fetch function
   * @returns Promise
   * @memberof PostFetcher
   */
  async getAllPublic({ setFlags, ignoreFlags, args = {} } = {}) {
    const allPosts = await this.getAll({ setFlags, ignoreFlags, args });

    // Filter out private posts
    const publicPosts = allPosts.data.filter((post) => !post.acf?.private);

    return Promise.resolve({ data: publicPosts });
  }

  /**
   *
   *
   * @param {Number} limit
   * @param {Boolean} setFlags - weather or not set "latestLoaded" flegs in store after fetch
   * @param {Boolean} ignoreFlags - weather or not consider "latestLoaded" store flag while checking store post status
   * @param {Object} args - additional args passed to fetch function
   * @returns Promise
   * @memberof PostFetcher
   */
  async getLatest({ limit = 3, args = {}, setFlags, ignoreFlags } = {}) {
    return this.queue.add(async () => {
      const storeState = this.store.getState();
      const store = storeState[this.storeKey];

      //this.log('PostFetcher::getLatest() -> storeState for "'+this.storeKey+'": ', store);

      if (store.latestLoaded[WP.lang] && ignoreFlags !== true) {
        // latest posts have already been loaded and they are already ordered by date - return

        this.log("PostFetcher::getLatest() -> latest " + limit + " posts already in store");

        return Promise.resolve({ data: store.posts[WP.lang].filter((p, i) => i < limit) });
      } else {
        if (store.posts[WP.lang].length > 0) {
          // some posts already in store (but not all) - fetch remaining

          this.log("PostFetcher::getLatest() -> some posts already in store - fetching rest...");

          let remaining = await this.getPosts({
            per_page: limit,
            exclude: this.collectPostIds(store.posts[WP.lang]).join(","),
            ...args,
          });

          this.log("PostFetcher::getLatest() -> ...fetch result (remaining posts): ", remaining);

          const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(remaining.data || [])]);

          this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store
          if (setFlags !== false) this.store.dispatch(this.actions.latestLoaded(true)); // update store flag

          return Promise.resolve({ data: combinedPosts });
        } else {
          // store does not have any posts - fetch latest 3

          this.log("PostFetcher::getLatest() -> no posts in store - fetching latest " + limit + "...");

          let latest = await this.getPosts({
            per_page: limit,
            ...args,
          });

          this.log("PostFetcher::getLatest() -> ...fetch result (latest posts): ", latest);

          this.store.dispatch(this.actions.store(latest.data || [])); // set posts in store
          if (setFlags !== false) this.store.dispatch(this.actions.latestLoaded(true)); // update store flag

          latest.data = this.orderPostsByDate(latest.data);
          return Promise.resolve(latest);
        }
      }
    });
  }

  /**
   * Gest post by slug
   *
   * @param {String} slug
   * @returns Promise
   * @memberof PostFetcher
   */
  async getBySlug({ slug = "" } = {}) {
    return this.queue.add(async () => {
      const storeState = this.store.getState();
      const store = storeState[this.storeKey];

      //this.log('PostFetcher::getBySlug() -> storeState for "'+this.storeKey+'": ', store);

      const postsMatchingslug = store.posts[WP.lang].filter((p) => (p.slug || p.post_name) === slug);

      if (postsMatchingslug[0] && !this.middleware.requiresFreshData()) {
        // found requested post in store - return it

        this.log('PostFetcher::getBySlug() -> post with slug "' + slug + '" already in store');

        return Promise.resolve({ data: postsMatchingslug });
      } else {
        // store does not contain requested posts - fetch 1 by slug

        if (!this.middleware.requiresFreshData())
          this.log('PostFetcher::getBySlug() -> post with slug "' + slug + '" was not found in store - fetching...');

        const single = await this.getPosts({
          slug,
          per_page: 1,
        });

        this.log("PostFetcher::getBySlug() -> ...fetch result (single post): ", single);

        if (this.middleware.requiresFreshData()) {
          this.log("PostFetcher::getBySlug() required fresh data");
          return Promise.resolve(single);
        }

        const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(single.data || [])]);

        this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store

        return Promise.resolve(single);
      }
    });
  }

  /**
   * Get post by specified IDS
   *
   * @param {Array} ids - arrat of post IDs to fetch
   * @param {Object} args - additional args passed to fetch function
   * @returns Promise
   * @memberof PostFetcher
   */
  async getByIds({ ids = [], args } = {}) {
    return this.queue.add(async () => {
      const storeState = this.store.getState();
      const store = storeState[this.storeKey];
      const requestedIds = ids;

      //this.log('PostFetcher::getByIds() -> storeState for "'+this.storeKey+'": ', store);

      if (store.posts[WP.lang].length > 0 && !this.middleware.requiresFreshData()) {
        // some posts already in store - check if ones we need are there
        const postsMatchingRequestedIds = store.posts[WP.lang].filter((p) => requestedIds.indexOf(Number(p.id)) !== -1);
        if (postsMatchingRequestedIds.length === requestedIds.length) {
          // all posts (by requested ID) are already in tore - return them

          this.log(
            "PostFetcher::getByIds() -> all requested posts (" + requestedIds.join(",") + ") are already in store"
          );

          return Promise.resolve({ data: postsMatchingRequestedIds });
        } else {
          if (postsMatchingRequestedIds.length > 0) {
            // some BUT NOT ALL posts were found in tore - fetch remaining

            this.log(
              "PostFetcher::getByIds() -> some posts from [" +
                requestedIds.join(",") +
                "] are already in store - fetching rest..."
            );

            let idsOfMatchingPostsFromStore = postsMatchingRequestedIds.map((p) => p.id);

            let remainingIds = requestedIds
              .filter((id) => idsOfMatchingPostsFromStore.indexOf(Number(id)) === -1)
              .join(",");

            let remaining = remainingIds
              ? await this.getPosts({
                  include: remainingIds,
                  ...args,
                })
              : [];

            this.log("PostFetcher::getByIds() -> ...fetch result (remaining posts): ", remaining);

            const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(remaining.data || [])]);

            this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store

            return Promise.resolve({ data: combinedPosts.filter((p) => requestedIds.indexOf(Number(p.id)) !== -1) });
          } else {
            // store does not contain any of the requested posts - fetch all that are requested

            this.log(
              "PostFetcher::getByIds() -> no posts from [" +
                requestedIds.join(",") +
                "] were found in store - fetching all requested..."
            );

            let requested = await this.getPosts({
              include: requestedIds.join(","),
              ...args,
            });

            this.log("PostFetcher::getByIds() -> ...fetch result (requested posts): ", requested);

            const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(requested.data || [])]);

            this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store

            return Promise.resolve(requested);
          }
        }
      } else {
        // store does not have any posts - fetch ones we need by ID

        if (!this.middleware.requiresFreshData())
          this.log("PostFetcher::getByIds() -> store does not have any posts - fetching all requested...");

        let requested = await this.getPosts({
          include: requestedIds.join(","),
          ...args,
        });

        this.log("PostFetcher::getByIds() -> ...fetch result (requested posts): ", requested);

        if (this.middleware.requiresFreshData()) {
          this.log("PostFetcher::getByIds() required fresh data");
          return Promise.resolve(requested);
        }

        const combinedPosts = this.orderPostsByDate([...store.posts[WP.lang], ...(requested.data || [])]);

        this.store.dispatch(this.actions.store(combinedPosts)); // set posts in store

        return Promise.resolve(requested);
      }
    });
  }
}

export default PostFetcher;
